import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Gauge } from '../common/charts/Gauge';
import { InjuryBadge } from '../common/InjuryBadge';
import type { PointTrackerModel, TrackerRow } from './pointTrackerViewModel';

interface PointTrackerProps {
    // undefined while the day's total is withheld or no game has started.
    points?: number;
    top: number;
    tracker: PointTrackerModel;
    // Shown in place of the player lists (loading, no games, locked scores).
    message?: ReactNode;
}

const Name = ({ row }: { row: TrackerRow }) => (
    <td className="left name">
        <div className="tracker-name">
            <span className="hl" title={row.player.displayName}>{row.name}</span><InjuryBadge level={row.player.injury} />
        </div>
    </td>
);

// The game day's points of our players on a gauge, and below it our skaters:
// those who have played or are playing, those not in the lineup and those whose
// game is still to come.
export function PointTracker({ points, top, tracker, message }: PointTrackerProps) {
    const { t } = useTranslation();
    const { played, notPlaying, upcoming, withheld } = tracker;
    const valueText = points === undefined ? '–' : t('points_short', { n: points });
    const empty = played.length === 0 && notPlaying.length === 0 && upcoming.length === 0;

    return (
        <section className="point-tracker" aria-label={t('tracker_title')} data-testid="point-tracker">
            <Gauge value={points} top={top} valueText={valueText} label={`${t('today_totalLabel')}: ${valueText}`} />
            {message ?? (
                <>
                    {withheld &&
                        <p className="players-note" data-testid="players-hidden-note" role="note">{t('today_hiddenTeamsPlayers')}</p>}
                    {empty && !withheld && <p className="state-message">{t('tracker_noPlayers')}</p>}
                    {played.length > 0 &&
                        <table className="dt tracker-table" data-testid="tracker-played">
                            <colgroup>
                                <col className="tracker-col-name" />
                                <col className="tracker-col-team" />
                                <col className="tracker-col-points" />
                                <col className="tracker-col-game" />
                                <col className="tracker-col-clock" />
                            </colgroup>
                            <thead className="visually-hidden">
                                <tr>
                                    <th scope="col">{t('col_player')}</th>
                                    <th scope="col">{t('scFull_team')}</th>
                                    <th scope="col">{t('scFull_points')}</th>
                                    <th scope="col">{t('col_game')}</th>
                                    <th scope="col">{t('tracker_clock')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {played.map(row => (
                                    <tr key={row.player.playerId}>
                                        <Name row={row} />
                                        <td className="left">{row.player.team}</td>
                                        <td className="strong">{row.goals}+{row.assists}</td>
                                        <td className="left tracker-game">{row.game}</td>
                                        <td className="tracker-clock">{row.clock}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>}
                    {notPlaying.length > 0 &&
                        <>
                            <h3 className="section-head">{t('tracker_notPlaying')}</h3>
                            <table className="dt tracker-table tracker-out" data-testid="tracker-not-playing">
                                <colgroup>
                                    <col className="tracker-col-name" />
                                    <col className="tracker-col-team" />
                                    <col className="tracker-col-rest" />
                                </colgroup>
                                <thead className="visually-hidden">
                                    <tr>
                                        <th scope="col">{t('col_player')}</th>
                                        <th scope="col">{t('scFull_team')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {notPlaying.map(row => (
                                        <tr key={row.player.playerId}>
                                            <Name row={row} />
                                            <td className="left">{row.player.team}</td>
                                            <td />
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>}
                    {upcoming.length > 0 &&
                        <>
                            <h3 className="section-head">{t('tracker_upcoming')}</h3>
                            <table className="dt tracker-table tracker-upcoming" data-testid="tracker-upcoming">
                                <colgroup>
                                    <col className="tracker-col-name" />
                                    <col className="tracker-col-team" />
                                    <col className="tracker-col-upcoming-game" />
                                    <col className="tracker-col-start" />
                                </colgroup>
                                <thead className="visually-hidden">
                                    <tr>
                                        <th scope="col">{t('col_player')}</th>
                                        <th scope="col">{t('scFull_team')}</th>
                                        <th scope="col">{t('col_game')}</th>
                                        <th scope="col">{t('tracker_start')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {upcoming.map(row => (
                                        <tr key={row.player.playerId}>
                                            <Name row={row} />
                                            <td className="left">{row.player.team}</td>
                                            <td className="left tracker-game">{row.game}</td>
                                            <td>{row.start}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>}
                </>
            )}
        </section>
    );
}
